import { useState } from 'react';
import { cardId, type Card, type Suit } from '@crew/shared';
import { useTheme } from '../theme.js';

/**
 * Suits are shapes first and colours second, so the deck stays readable for
 * colourblind players and when a card is dimmed.
 */
export function SuitIcon({ suit, className }: { suit: Suit; className?: string }) {
  const common = { viewBox: '0 0 24 24', className, 'aria-hidden': true } as const;
  switch (suit) {
    case 'pink':
      return (
        <svg {...common}>
          <path d="M12 2 22 12 12 22 2 12Z" fill="currentColor" />
        </svg>
      );
    case 'green':
      return (
        <svg {...common}>
          <path d="M12 2 23 21H1Z" fill="currentColor" />
        </svg>
      );
    case 'blue':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" fill="currentColor" />
        </svg>
      );
    case 'yellow':
      return (
        <svg {...common}>
          <rect x="2.5" y="2.5" width="19" height="19" rx="2.5" fill="currentColor" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path
            d="M12 1c3.2 2.6 5 6.4 5 10.4v3.1l2.4 2.4a1 1 0 0 1 .3.7V21l-4-1.6h-7.4L4 21v-3.4a1 1 0 0 1 .3-.7L7 14.5v-3.1C7 7.4 8.8 3.6 12 1Z"
            fill="currentColor"
          />
          <circle cx="12" cy="9.5" r="2.1" fill="#0a1020" />
        </svg>
      );
  }
}

export interface PlayingCardProps {
  card: Card;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Recede rather than desaturate, so suit identity survives. */
  muted?: boolean;
  /** Legal moves rise and glow. */
  highlight?: boolean;
  selected?: boolean;
  badge?: string | null;
  onClick?: () => void;
  title?: string;
  disabled?: boolean;
}

export function PlayingCard({
  card,
  size = 'md',
  muted = false,
  highlight = false,
  selected = false,
  badge = null,
  onClick,
  title,
  disabled = false
}: PlayingCardProps) {
  const className = [
    'card',
    `card--${size}`,
    `suit-${card.suit}`,
    muted ? 'is-muted' : '',
    highlight ? 'is-legal' : '',
    selected ? 'is-selected' : '',
    onClick && !disabled ? 'is-clickable' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const label = card.suit === 'rocket' ? `rocket ${card.rank}` : `${card.suit} ${card.rank}`;

  const face = (
    <>
      <SuitIcon suit={card.suit} className="card__pip card__pip--tl" />
      <span className="card__rank">{card.rank}</span>
      <SuitIcon suit={card.suit} className="card__pip card__pip--br" />
      {badge ? <span className="card__badge">{badge}</span> : null}
    </>
  );

  if (!onClick) {
    return (
      <div className={className} title={title ?? label} role="img" aria-label={label}>
        {face}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      aria-label={label}
    >
      {face}
    </button>
  );
}
